/**
 * Создает экземпляр HTTP-клиента
 * @param {Object} config - Конфигурация клиента
 * @param {string} config.baseURL - Базовый URL
 * @param {Object} config.headers - Общие заголовки
 * @param {number} config.timeout - Таймаут в мс (по умолчанию 5000)
 * @returns {Object} - Объект с методами для HTTP-запросов
 */
function createHttpClient(config = {}) {
    const BASE_URL = 'https://api.traders-bay.ru';
    const { baseURL = BASE_URL, headers = {}, timeout = 5000 } = config;

    /**
     * Обрабатывает ошибки HTTP-запросов
     * @param {Response} response - Ответ сервера
     * @returns {Promise} - Промис с ошибкой или ответом
     */
    async function handleResponse(response) {
        if (!response.ok) {
            const error = new Error(`HTTP error! status: ${response.status}`);
            error.status = response.status;
            try {
                error.data = await parseResponse(response);
            } catch (e) {
                error.data = await response.text();
            }
            throw error;
        }
        return parseResponse(response);
    }

    /**
     * Парсит ответ сервера
     * @param {Response} response - Ответ сервера
     * @returns {Promise<Object|string>} - Распарсенные данные
     */
    async function parseResponse(response) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            return response.json();
        }
        return response.text();
    }

    /**
     * Выполняет HTTP-запрос
     * @param {string} url - URL запроса
     * @param {string} method - HTTP-метод
     * @param {Object} options - Опции запроса
     * @returns {Promise} - Промис с результатом запроса
     */
    async function request(url, method, options = {}) {
        const {
            headers: requestHeaders = {},
            body,
            params = {},
            timeout: requestTimeout = timeout,
            ...otherOptions
        } = options;

        // Формируем URL с query-параметрами
        const queryString = Object.keys(params).length
            ? `?${new URLSearchParams(params).toString()}`
            : '';
        const fullUrl = `${baseURL}${url}${queryString}`;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

        const token = localStorage.getItem('authToken');
        if (token) headers['Authorization'] = `Bearer ${token}`;

        try {
            window.__log('ajax ' + method, fullUrl, {body: body ? JSON.stringify(body) : undefined});
            const response = await fetch(fullUrl, {
                method,
                headers: { ...headers, ...requestHeaders },
                body: body ? JSON.stringify(body) : undefined,
                signal: controller.signal,
                ...otherOptions,
            });

            clearTimeout(timeoutId);
            return await handleResponse(response);
        } catch (error) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') {
                throw new Error(`Request timeout (${requestTimeout}ms)`);
            }
            throw error;
        }
    }

    // Возвращаем методы для работы с HTTP
    return {
        /**
         * GET-запрос
         * @param {string} url - URL запроса
         * @param {Object} options - Опции запроса
         * @returns {Promise} - Промис с результатом
         */
        get(url, options) {
            return request(url, 'GET', options);
        },

        /**
         * POST-запрос
         * @param {string} url - URL запроса
         * @param {Object} body - Тело запроса
         * @param {Object} options - Опции запроса
         * @returns {Promise} - Промис с результатом
         */
        post(url, body, options) {
            return request(url, 'POST', { ...options, body });
        },

        /**
         * PUT-запрос
         * @param {string} url - URL запроса
         * @param {Object} body - Тело запроса
         * @param {Object} options - Опции запроса
         * @returns {Promise} - Промис с результатом
         */
        put(url, body, options) {
            return request(url, 'PUT', { ...options, body });
        },

        /**
         * PATCH-запрос
         * @param {string} url - URL запроса
         * @param {Object} body - Тело запроса
         * @param {Object} options - Опции запроса
         * @returns {Promise} - Промис с результатом
         */
        patch(url, body, options) {
            return request(url, 'PATCH', { ...options, body });
        },

        /**
         * DELETE-запрос
         * @param {string} url - URL запроса
         * @param {Object} options - Опции запроса
         * @returns {Promise} - Промис с результатом
         */
        delete(url, options) {
            return request(url, 'DELETE', options);
        },
    };
}

window.ajax = createHttpClient({
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 8000,
});

// Пример использования
// const api = createHttpClient({
//     baseURL: 'https://api.example.com',
//     headers: {
//         'Content-Type': 'application/json',
//         Authorization: 'Bearer your_token_here',
//     },
//     timeout: 8000,
// });

// // GET запрос
// api.get('/users', { params: { page: 1 } })
//     .then(data => console.log(data))
//     .catch(error => console.error('Error:', error));

// // POST запрос
// api.post('/users', { name: 'John', age: 30 })
//     .then(data => console.log(data))
//     .catch(error => console.error('Error:', error));